import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useEffect, useMemo, useRef } from 'react';
import cn from 'classnames';
import { BottomSheet, BottomSheetContentLayout, Drawer, Typography } from 'seagull';
import { Box } from 'seagull/components/Layout';
import { getTestIds } from 'seagull/utils/TestIds';
import { useIsMobileForHomePage } from '~/hooks/useIsMobileForHomePage';
import { useLocaleKeys } from '~/i18n';
import { useCancelTask } from '../hooks/useCancelTask';
import { useMarkTasksSeen } from '../hooks/useMarkTasksSeen';
import { useTasksJobs } from '../hooks/useTasksJobs';
import { isTerminal } from '../taskModel';
import { useTasksDrawer } from '../tasksDrawerStore';
import styles from './TasksDrawer.module.scss';
import { TaskItem } from './TasksSections/TaskItem/TaskItem';
import { TasksSections } from './TasksSections/TasksSections';
export const GetTasksDrawerTID = (parentTestId)=>getTestIds(parentTestId);
export const TasksDrawer = (param)=>{
    let { testId } = param;
    const { t: texts } = useLocaleKeys('tasksDrawer');
    const isMobileDevice = useIsMobileForHomePage();
    const { isDrawerOpen, setIsDrawerOpen } = useTasksDrawer();
    const { tasks } = useTasksJobs();
    const { markSeen } = useMarkTasksSeen();
    const { cancelTask } = useCancelTask();
    const { TasksDrawerTID } = useMemo(()=>GetTasksDrawerTID(testId), [
        testId
    ]);
    const markSeenAttemptedRef = useRef(new Set());
    useEffect(()=>{
        if (!isDrawerOpen) {
            markSeenAttemptedRef.current = new Set();
            return;
        }
        const toMark = tasks.filter((t)=>isTerminal(t.status) && !t.seen && !markSeenAttemptedRef.current.has(t.jobId)).map((t)=>t.jobId);
        if (toMark.length === 0) return;
        toMark.forEach((jobId)=>markSeenAttemptedRef.current.add(jobId));
        markSeen(toMark);
    }, [
        isDrawerOpen,
        tasks,
        markSeen
    ]);
    const onClose = useCallback(()=>setIsDrawerOpen(false), [
        setIsDrawerOpen
    ]);
    const headerTitle = texts.header.title();
    const sections = /*#__PURE__*/ _jsx(TasksSections, {
        renderTask: (task)=>/*#__PURE__*/ _jsx(TaskItem, {
                task: task,
                cancelTask: cancelTask
            }),
        testId: TasksDrawerTID.Root()
    });
    const footerDisclaimer = (sticky)=>/*#__PURE__*/ _jsx(Box, {
            className: cn(styles.footer, {
                [styles.sticky]: sticky
            }),
            padding: 16,
            testId: TasksDrawerTID.Footer(),
            children: /*#__PURE__*/ _jsx(Typography, {
                variant: "paragraph3",
                skin: "secondary",
                children: texts.footer.disclaimer()
            })
        });
    if (isMobileDevice) {
        return /*#__PURE__*/ _jsx(BottomSheet, {
            open: isDrawerOpen,
            onClose: onClose,
            testId: TasksDrawerTID.MobileSheet(),
            paddingBottom: 0,
            children: /*#__PURE__*/ _jsxs(BottomSheetContentLayout, {
                header: {
                    title: headerTitle,
                    titleTestId: TasksDrawerTID.Title(),
                    paddingBottom: 8
                },
                children: [
                    sections,
                    footerDisclaimer(true)
                ]
            })
        });
    }
    return /*#__PURE__*/ _jsx(Drawer, {
        open: isDrawerOpen,
        onClose: onClose,
        onCloseButtonClick: onClose,
        hasTopbar: true,
        header: /*#__PURE__*/ _jsx(Typography, {
            variant: "h5",
            weight: "bold",
            testId: TasksDrawerTID.Title(),
            children: headerTitle
        }),
        footer: footerDisclaimer(false),
        testId: TasksDrawerTID.DesktopPaper(),
        BackdropProps: {
            invisible: true
        },
        PaperProps: {
            style: {
                width: 444
            }
        },
        children: sections
    });
};
